// Lab Recommendations hooks using the real API with polling
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useLazyGetV2ProviderLabsRecommendationsByTaskIdQuery,
  usePostV2ProviderLabsRecommendationsMutation,
} from '../store/api';
import type { CreateLabOrderRequest, LabOrderRecommendation } from '../types';

interface UseLabRecommendationsOptions {
  enabled?: boolean;
  onSuccess?: (response: LabOrderRecommendation) => void;
  onError?: (error: Error) => void;
  pollingInterval?: number; // milliseconds
}

interface UseLabRecommendationsState {
  loading: boolean;
  data: LabOrderRecommendation | null;
  error: Error | null;
  taskId: string | null;
}

export const useLabRecommendations = (
  request: { note_id?: string | null; patient_id: string } | null,
  options: UseLabRecommendationsOptions = {},
) => {
  const {
    enabled = true,
    onSuccess,
    onError,
    pollingInterval = 2000, // Poll every 2 seconds
  } = options;

  const [state, setState] = useState<UseLabRecommendationsState>({
    loading: false,
    data: null,
    error: null,
    taskId: null,
  });

  const [startRecommendation] = usePostV2ProviderLabsRecommendationsMutation();
  const [triggerPoll] = useLazyGetV2ProviderLabsRecommendationsByTaskIdQuery();
  const hasStartedRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  // Start the recommendation process
  const startProcess = useCallback(async () => {
    if (!request || !enabled || hasStartedRef.current) return;

    hasStartedRef.current = true;
    setState({ loading: true, data: null, error: null, taskId: null });

    try {
      const response = await startRecommendation({
        startLabRecommendationRequest: request,
      }).unwrap();

      currentTaskIdRef.current = response.task_id;
      setState(prev => ({ ...prev, taskId: response.task_id }));
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to start lab recommendations');
      setState({ loading: false, data: null, error: err, taskId: null });
      onError?.(err);
      hasStartedRef.current = false;
    }
  }, [request, enabled, startRecommendation, onError]);

  // Stop polling helper
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    currentTaskIdRef.current = null;
  }, []);

  // Start manual polling when we have a taskId
  useEffect(() => {
    if (!state.taskId || !currentTaskIdRef.current) return;

    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const taskId = currentTaskIdRef.current;
    
    // Poll function
    const poll = async () => {
      // Double-check we still have a valid taskId
      if (!currentTaskIdRef.current) return;
      
      try {
        const result = await triggerPoll({ taskId }).unwrap();

        // Process the response based on status
        if (result.status === 'completed') {
          stopPolling();
          // Just pass through the data from the server
          const recommendation: LabOrderRecommendation = result as LabOrderRecommendation;
          setState({ loading: false, data: recommendation, error: null, taskId: null });
          if (recommendation.lab_order) {
            onSuccess?.(recommendation);
          }
          hasStartedRef.current = false;
        } else if (result.status === 'failed' || result.status === 'error') {
          stopPolling();
          const err = new Error(result.error || 'Lab recommendations failed');
          setState({ loading: false, data: null, error: err, taskId: null });
          onError?.(err);
          hasStartedRef.current = false;
        }
        // If status is 'pending', continue polling via interval
      } catch (error) {
        // Handle network errors
        console.error('Polling error:', error);
      }
    };

    // Initial poll
    poll();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      void poll();
    }, pollingInterval);

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [state.taskId, triggerPoll, stopPolling, pollingInterval, onSuccess, onError]);

  // Start the process when request changes
  useEffect(() => {
    if (
      request &&
      enabled &&
      !state.loading &&
      !state.data &&
      !state.error &&
      !hasStartedRef.current
    ) {
      startProcess();
    }
  }, [request, enabled, state.loading, state.data, state.error, startProcess]);

  // Reset when request changes
  useEffect(() => {
    hasStartedRef.current = false;
    // Also reset error state to allow retry with new request
    setState(prev => ({ ...prev, error: null }));
  }, [request]);

  const cancel = useCallback(() => {
    stopPolling();
    setState(prev => ({ ...prev, loading: false, taskId: null }));
    hasStartedRef.current = false;
  }, [stopPolling]);

  return {
    ...state,
    refetch: startProcess,
    cancel,
  };
};

// Utility to merge lab recommendations with user input
export const mergeWithLabRecommendations = (
  userInput: Partial<CreateLabOrderRequest>,
  aiRecommendation: LabOrderRecommendation,
  strategy: 'replace' | 'merge' | 'append' = 'merge',
): Partial<CreateLabOrderRequest> => {
  if (aiRecommendation.status !== 'completed' || !aiRecommendation.lab_order) {
    return userInput;
  }

  const { lab_tests, icd_10_codes } = aiRecommendation.lab_order;

  switch (strategy) {
    case 'replace':
      return {
        ...userInput,
        lab_tests,
        icd_10_codes,
      };

    case 'append':
      return {
        ...userInput,
        lab_tests: [...(userInput.lab_tests || []), ...lab_tests],
        icd_10_codes: [...(userInput.icd_10_codes || []), ...icd_10_codes],
      };

    case 'merge':
    default: {
      // Merge intelligently - combine arrays, remove duplicates
      const mergedTests = Array.from(new Set([...(userInput.lab_tests || []), ...lab_tests]));
      const mergedCodes = Array.from(new Set([...(userInput.icd_10_codes || []), ...icd_10_codes]));

      return {
        ...userInput,
        lab_tests: mergedTests,
        icd_10_codes: mergedCodes,
      };
    }
  }
};
